// app/javascript/controllers/nested_fields_controller.js
import { Controller } from '@hotwired/stimulus'

// Keep in sync with Billing::ContractProgram::HYBRID_DELIVERY_COUNTRIES
const HYBRID_DELIVERY_COUNTRIES = ['CA', 'BB', 'JM', 'PR', 'TT', 'VI']

export default class extends Controller {
  static targets = ['template', 'container', 'row']

  static values = {
    placeholder: {
      type: String,
      default: 'NEW_RECORD',
    },
  }

  add() {
    const index = this.nextIndex()
    const pattern = new RegExp(this.escapeRegExp(this.placeholderValue), 'g')

    const html = this.templateTarget.innerHTML.replace(pattern, index)

    this.containerTarget.insertAdjacentHTML('beforeend', html)
  }

  remove(event) {
    const row = event.target.closest('[data-nested-fields-target~="row"]')

    if (!row) return

    const idInput = row.querySelector('input[name$="[id]"]')

    if (idInput?.value) {
      const destroyInput = row.querySelector('input[name$="[_destroy]"]')

      if (destroyInput) {
        destroyInput.value = '1'
      }

      row.classList.add('hidden')
    } else {
      row.remove()
    }
  }

  syncUntilLandingSupport(event) {
    if (!event?.target) return

    this.syncUntilLandingSupportForRow(this.rowForTarget(event.target))
  }

  syncUntilLandingSupportForRow(row) {
    if (!row) return

    const programLevel = row.querySelector(
      '[data-nested-fields-target~="programLevel"]',
    )?.value
    const programDuration = row.querySelector(
      '[data-nested-fields-target~="programDuration"]',
    )?.value
    const untilLandingSupport = row.querySelector(
      '[data-nested-fields-target~="untilLandingSupport"]',
    )

    if (!untilLandingSupport) return

    if (this.shouldDefaultUntilLandingSupport(programLevel, programDuration)) {
      untilLandingSupport.value = 'true'
    } else {
      untilLandingSupport.value = 'false'
    }
  }

  // Global-section rows only allow a country-controlled delivery mode choice
  // (Domestic/YNS-direct vs Global/CSG-partner) for hybrid countries; every other
  // country must be delivered globally.
  syncDeliveryMode(event) {
    if (!event?.target) return

    this.syncDeliveryModeForRow(this.rowForTarget(event.target))
  }

  syncDeliveryModeForRow(row) {
    if (!row) return

    const country = row.querySelector(
      '[data-nested-fields-target~="country"]',
    )?.value
    const deliveryMode = row.querySelector(
      '[data-nested-fields-target~="deliveryMode"]',
    )

    if (!deliveryMode) return

    if (HYBRID_DELIVERY_COUNTRIES.includes(country)) {
      deliveryMode.disabled = false
    } else {
      deliveryMode.value = 'global'
      deliveryMode.disabled = true
    }
  }

  nextIndex() {
    const rows = this.rowTargets
    if (rows.length === 0) return 0

    const indices = rows
      .map((row) => row.dataset.index)
      .filter((index) => index !== undefined)

    if (indices.length === 0) return 0

    const maxIndex = Math.max(...indices.map((index) => parseInt(index)))
    return maxIndex + 1
  }

  escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  rowForTarget(target) {
    return target.closest('[data-nested-fields-target~="row"]')
  }

  shouldDefaultUntilLandingSupport(programLevel, programDuration) {
    return (
      (programLevel === 'SILVER' && programDuration === '6') ||
      (programLevel === 'GOLD' && programDuration === '6') ||
      (programLevel === 'SELECT' && programDuration === '12')
    )
  }
}
